import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { NotificationSource, TransportOrderNotificationListItemDto } from "../../../models/transport/transportOrderNotification";
import { getTransportOrderNotifications, confirmTransportOrderNotification } from "../../../services/transportOrderNotificationService";

export interface NotificationState {
    unconfirmedCount: number;
    lastUnconfirmedCount: number;
    notifications: TransportOrderNotificationListItemDto[];
    loading: boolean;
}

const initialState: NotificationState = {
    unconfirmedCount: 0,
    lastUnconfirmedCount: 0,
    notifications: [],
    loading: false,
};

export const fetchNotifications = createAsyncThunk(
    "notification/fetchNotifications",
    async(employeeId: string, { rejectWithValue }) => {
        try {
            const response = await getTransportOrderNotifications({
                employeeId: employeeId,
                source: NotificationSource.Desktop
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching notifications:", error);
            return rejectWithValue(error);
        }
    }
);

export const updateNotification = createAsyncThunk(
    "notification/updateNotification",
    async(
        { transportOrderNotificationId }: { transportOrderNotificationId: string },
        { rejectWithValue }
    ) => {
        try {
            await confirmTransportOrderNotification(transportOrderNotificationId);
            return transportOrderNotificationId;
        } catch (error) {
            console.error("Error confirming notification:", error);
            return rejectWithValue(error);
        }
    }
);

export const notificationSlice = createSlice({
    name: "notification",
    initialState,
    reducers: {
        setNotifications(state, action: PayloadAction<TransportOrderNotificationListItemDto[]>) {
            state.notifications = action.payload;
            state.unconfirmedCount = action.payload.length;
        },
        clearNotifications(state) {
            state.notifications = [];
            state.unconfirmedCount = 0;
            state.lastUnconfirmedCount = 0;
        },
        setLastUnconfirmedCount(state, action: PayloadAction<number>) {
            state.lastUnconfirmedCount = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchNotifications.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchNotifications.fulfilled, (state, action: PayloadAction<TransportOrderNotificationListItemDto[]>) => {
            state.notifications = action.payload;
            state.unconfirmedCount = action.payload.length;
            state.loading = false;
        });
        builder.addCase(fetchNotifications.rejected, (state) => {
            state.loading = false;
        });

        builder.addCase(updateNotification.fulfilled, (state, action: PayloadAction<string>) => {
            state.notifications = state.notifications.filter(
                (notification) => notification.id !== action.payload
            );
            state.unconfirmedCount = state.notifications.length;
        });
    },
});

export const notificationReducer = notificationSlice.reducer;

export const { setNotifications, clearNotifications, setLastUnconfirmedCount } = notificationSlice.actions;
